import React, { useEffect, useState } from 'react';
import styles from './DetailSection.module.css';
import {
    OptimizedMotion,
    OptimizedMotionH1,
    OptimizedMotionP,
} from '../../../shared/ui/optimized-motion/OptimizedMotion';

import IconPricingFeature from '../../../shared/ui/icons/IconPricingFeature';
import IconPricingRocket from '../../../shared/ui/icons/IconPricingRocket';
import { useSelector } from 'react-redux';
import { fetchSubscriptionProducts, StripeProduct } from '../../../store/slices/subscriptionSlice';
import { RootState, useAppDispatch } from '../../../store/store';
import Loader from '../../../shared/ui/loader/Loader';
import SubscriptionConfirmationModal from '../../setting-page/component/subscription/SubscriptionConfirmationModal';

const DetailSection: React.FC = () => {
    const { products, isLoading, userSubscription } = useSelector(
        (state: RootState) => state.subscription,
    );

    const [selectedProduct, setSelectedProduct] = useState<StripeProduct | null>(null);
    const [isOpen, setIsOpen] = useState<boolean>(false);
    const redirectUrl = window.location.origin + window.location.pathname;

    const dispatch = useAppDispatch();

    useEffect(() => {
        if (!products.length) {
            dispatch(fetchSubscriptionProducts());
        }
    }, [dispatch]);

    const handleBuy = async (product: StripeProduct) => {
        setSelectedProduct(product);
        setIsOpen(true);
    };

    const currentTier = userSubscription?.tier ?? 0;

    return (
        <>
            {isLoading && <Loader />}

            <div className={styles.container}>
                <OptimizedMotionH1 className={styles.title} type="slide-down" delay={0.2}>
                    Our pricing plans
                </OptimizedMotionH1>
                <OptimizedMotionP className={styles.description} type="slide-down" delay={0.4}>
                    Cancel anytime • No hidden fees • You own your content &amp; data.
                </OptimizedMotionP>

                <div className={styles.pricingContainer}>
                    {products
                        .slice()
                        .sort((a, b) => {
                            const orderA = Number(a?.attributes?.metadata?.tier ?? 0);
                            const orderB = Number(b?.attributes?.metadata?.tier ?? 0);
                            return orderA - orderB;
                        })
                        .map((product, index) => {
                            const plan = product.attributes;
                            const price = plan.unit_amount || '$0';
                            const note = plan.metadata?.note;

                            const productTier = Number(product?.attributes?.metadata?.tier ?? 0);

                            const rawPlan =
                                product?.attributes?.metadata?.plan ||
                                product?.attributes?.name ||
                                'Plan';
                            const productLabel = rawPlan.charAt(0).toUpperCase() + rawPlan.slice(1);

                            let buttonLabel = 'Subscribe';

                            if (currentTier === productTier) {
                                buttonLabel = 'Manage Plan';
                            } else if (productTier < currentTier) {
                                buttonLabel = `${userSubscription?.is_active ? 'Downgrade' : 'Subscribe'} to ${productLabel}`;
                            } else if (productTier > currentTier) {
                                buttonLabel = `${userSubscription?.is_active ? 'Upgrade' : 'Subscribe'} to ${productLabel}`;
                            }

                            return (
                                <OptimizedMotion
                                    key={product.id}
                                    className={[
                                        styles.card,
                                        !userSubscription?.is_active && plan.metadata?.is_popular
                                            ? styles.cardFeatured
                                            : userSubscription?.is_active &&
                                                plan.metadata?.tier == currentTier
                                              ? styles.cardFeatured
                                              : '',
                                    ].join(' ')}
                                    type="slide-up"
                                    delay={0.2 + index * 0.15}
                                    duration={0.4}
                                >
                                    <div className={styles.iconRow}>
                                        <div>
                                            <p className={styles.planLabel}>{plan.name}</p>
                                            <h3 className={styles.planTitle}>{plan.description}</h3>
                                        </div>
                                    </div>

                                    <ul className={styles.featureList}>
                                        {plan.features?.map((feature: string, i: number) => (
                                            <li key={i} className={styles.featureItem}>
                                                <span aria-hidden="true">
                                                    <IconPricingFeature />
                                                </span>
                                                {feature}
                                            </li>
                                        ))}
                                    </ul>
                                    {(plan.name === 'SimpleeCreate - Basic Plan' ||
                                        plan.name === 'SimpleeCreate - Premium Plan') && (
                                        <div className={styles.freeTrial}>Free 3-day trial</div>
                                    )}

                                    {note && <div className={styles.priceNote}>{note}</div>}
                                    <div className={styles.priceRow}>
                                        <span className={styles.priceValue}>${price}</span>
                                        <span className={styles.priceUnit}>/ month</span>
                                    </div>
                                    <button
                                        onClick={() => handleBuy(product)}
                                        className={
                                            !userSubscription?.is_active &&
                                            plan.metadata?.is_popular
                                                ? styles.btnPrimary
                                                : userSubscription?.is_active &&
                                                    plan.metadata?.tier > currentTier
                                                  ? styles.btnPrimary
                                                  : styles.btnGhost
                                        }
                                    >
                                        {plan.metadata?.tier > currentTier && (
                                            <span className={styles.rocketClass}>
                                                <IconPricingRocket />
                                            </span>
                                        )}{' '}
                                        {buttonLabel}
                                    </button>
                                </OptimizedMotion>
                            );
                        })}
                </div>
            </div>

            <SubscriptionConfirmationModal
                product={selectedProduct}
                autoRenew={true}
                isOpen={isOpen}
                setIsOpen={setIsOpen}
                inputRedirectUrl={redirectUrl}
            />
        </>
    );
};

export default DetailSection;
